import {
  deltaForDirection,
  directionForDelta,
  type Direction,
} from './direction';
import type { TilePoint } from './mobile';
import { canStepForLevel, walkableForLevel } from './walkability';
import type { World } from './world';

/** Dungeon action-mode locomotion keeps the ordinary 250/125 ms walk/run gaits. */
export const DUNGEON_PLAYER_WALK_SPEED_TILES_PER_SECOND = 4;
export const DUNGEON_PLAYER_RUN_SPEED_TILES_PER_SECOND = 8;

/** Radius of the dungeon player capsule on the flat collision plane. */
export const DUNGEON_PLAYER_COLLISION_RADIUS_TILES = 0.28;

/** Maximum distance of one swept collision probe. */
export const DUNGEON_PLAYER_COLLISION_SUBDIVISION_TILES = 0.125;

/** Enemy sweeps share the proven player subdivision without sharing networking. */
export const DUNGEON_ENEMY_COLLISION_SUBDIVISION_TILES =
  DUNGEON_PLAYER_COLLISION_SUBDIVISION_TILES;

/** Existing Skeleton Warrior gait, retained as the enemy steering speed source. */
export const DUNGEON_ENEMY_MS_PER_TILE = 320;
export const DUNGEON_ENEMY_SPEED_TILES_PER_SECOND =
  1000 / DUNGEON_ENEMY_MS_PER_TILE;

/** Radius of a dungeon enemy on the flat collision plane. */
export const DUNGEON_ENEMY_COLLISION_RADIUS_TILES = 0.3;

/** Exact center-to-center distance at which a pursuing melee enemy parks. */
export const DUNGEON_ENEMY_MELEE_STOPPING_RANGE_TILES = 1;

/** Drift below this distance is ordinary prediction noise and is left untouched. */
export const DUNGEON_RECONCILIATION_TOLERANCE_TILES = 0.12;

/** Smooth acceleration and settlement shared by Charge prediction and FX timing. */
export function dungeonRushProgress(progress: number): number {
  const t = Math.max(0, Math.min(1, progress));
  return t * t * (3 - 2 * t);
}

export interface DungeonMovementIntent {
  dir: Direction | null;
  running: boolean;
}

export interface ContinuousPoint {
  x: number;
  y: number;
}

export interface DungeonCollisionCircle extends ContinuousPoint {
  radius: number;
}

export interface DungeonMovementResult extends ContinuousPoint {
  facing?: Direction;
}

export interface DungeonEnemyRouteResult extends DungeonMovementResult {
  route: TilePoint[];
  moving: boolean;
}

export function dungeonCellFor(point: ContinuousPoint): TilePoint {
  return {
    x: Math.round(point.x),
    y: Math.round(point.y),
  };
}

function circleOverlapsCell(
  point: ContinuousPoint,
  radius: number,
  cellX: number,
  cellY: number,
): boolean {
  const nearestX = Math.max(cellX - 0.5, Math.min(point.x, cellX + 0.5));
  const nearestY = Math.max(cellY - 0.5, Math.min(point.y, cellY + 0.5));
  return Math.hypot(point.x - nearestX, point.y - nearestY) < radius - 1e-9;
}

/** Whether the whole supplied-radius actor circle can occupy a dungeon point. */
export function canOccupyDungeonPosition(
  world: World,
  point: ContinuousPoint,
  level = 0,
  radius = DUNGEON_PLAYER_COLLISION_RADIUS_TILES,
): boolean {
  const walkable = walkableForLevel(world, level);
  const minX = Math.floor(point.x - radius + 0.5);
  const maxX = Math.ceil(point.x + radius - 0.5);
  const minY = Math.floor(point.y - radius + 0.5);
  const maxY = Math.ceil(point.y + radius - 0.5);
  for (let y = minY; y <= maxY; y += 1) {
    for (let x = minX; x <= maxX; x += 1) {
      if (!walkable(x, y) && circleOverlapsCell(point, radius, x, y)) {
        return false;
      }
    }
  }
  return true;
}

function canMoveBetween(
  world: World,
  from: ContinuousPoint,
  to: ContinuousPoint,
  level: number,
  radius: number,
  obstacles: readonly DungeonCollisionCircle[],
): boolean {
  if (!canOccupyDungeonPosition(world, to, level, radius)) return false;
  if (
    obstacles.some(
      (obstacle) => {
        const required = radius + obstacle.radius;
        const fromDistance = Math.hypot(
          from.x - obstacle.x,
          from.y - obstacle.y,
        );
        const toDistance = Math.hypot(to.x - obstacle.x, to.y - obstacle.y);
        return (
          toDistance < required - 1e-9 &&
          !(
            fromDistance < required - 1e-9 &&
            toDistance > fromDistance + 1e-9
          )
        );
      },
    )
  ) {
    return false;
  }
  const fromCell = dungeonCellFor(from);
  const toCell = dungeonCellFor(to);
  if (fromCell.x === toCell.x && fromCell.y === toCell.y) return true;
  return canStepForLevel(world, level)(fromCell, toCell);
}

function furthestReachable(
  world: World,
  from: ContinuousPoint,
  to: ContinuousPoint,
  level: number,
  radius: number,
  obstacles: readonly DungeonCollisionCircle[],
): ContinuousPoint {
  if (canMoveBetween(world, from, to, level, radius, obstacles)) return to;
  let low = 0;
  let high = 1;
  for (let iteration = 0; iteration < 14; iteration += 1) {
    const progress = (low + high) / 2;
    const candidate = {
      x: from.x + (to.x - from.x) * progress,
      y: from.y + (to.y - from.y) * progress,
    };
    if (canMoveBetween(world, from, candidate, level, radius, obstacles)) {
      low = progress;
    } else {
      high = progress;
    }
  }
  return {
    x: from.x + (to.x - from.x) * low,
    y: from.y + (to.y - from.y) * low,
  };
}

/**
 * Sweep one continuous displacement through the dungeon collision grid.
 *
 * The move is subdivided to prevent tunnelling. A blocked diagonal first tries
 * each component independently, which produces wall sliding without allowing a
 * radius-sized capsule to cut a blocked corner.
 */
export function sweepDungeonDisplacement(
  world: World,
  start: ContinuousPoint,
  displacement: ContinuousPoint,
  options: {
    level?: number;
    radius?: number;
    subdivision?: number;
    slide?: boolean;
    obstacles?: readonly DungeonCollisionCircle[];
  } = {},
): ContinuousPoint {
  const level = options.level ?? 0;
  const radius = options.radius ?? DUNGEON_PLAYER_COLLISION_RADIUS_TILES;
  const subdivision =
    options.subdivision ?? DUNGEON_PLAYER_COLLISION_SUBDIVISION_TILES;
  const obstacles = options.obstacles ?? [];
  const distance = Math.hypot(displacement.x, displacement.y);
  if (distance <= 0 || !Number.isFinite(distance)) return { ...start };
  const steps = Math.max(1, Math.ceil(distance / subdivision));
  const stepX = displacement.x / steps;
  const stepY = displacement.y / steps;
  let point = { ...start };
  for (let step = 0; step < steps; step += 1) {
    const diagonal = { x: point.x + stepX, y: point.y + stepY };
    if (canMoveBetween(world, point, diagonal, level, radius, obstacles)) {
      point = diagonal;
      continue;
    }
    if (options.slide === false) {
      point = furthestReachable(
        world,
        point,
        diagonal,
        level,
        radius,
        obstacles,
      );
      break;
    }
    const alongX = { x: point.x + stepX, y: point.y };
    if (stepX !== 0) {
      point = furthestReachable(
        world,
        point,
        alongX,
        level,
        radius,
        obstacles,
      );
    }
    const alongY = { x: point.x, y: point.y + stepY };
    if (stepY !== 0) {
      point = furthestReachable(
        world,
        point,
        alongY,
        level,
        radius,
        obstacles,
      );
    }
  }
  return point;
}

/** Integrate one held dungeon movement intent for an elapsed duration. */
export function integrateDungeonMovement(
  world: World,
  start: ContinuousPoint,
  intent: DungeonMovementIntent,
  elapsedMs: number,
  level = 0,
): DungeonMovementResult {
  if (intent.dir === null || elapsedMs <= 0 || !Number.isFinite(elapsedMs)) {
    return { x: start.x, y: start.y };
  }
  const delta = deltaForDirection(intent.dir);
  const magnitude = Math.hypot(delta.dx, delta.dy);
  const speed = intent.running
    ? DUNGEON_PLAYER_RUN_SPEED_TILES_PER_SECOND
    : DUNGEON_PLAYER_WALK_SPEED_TILES_PER_SECOND;
  const distance = speed * elapsedMs / 1000;
  const position = sweepDungeonDisplacement(
    world,
    start,
    {
      x: delta.dx / magnitude * distance,
      y: delta.dy / magnitude * distance,
    },
    { level },
  );
  return { ...position, facing: intent.dir };
}

function separateDungeonCircles(
  world: World,
  start: ContinuousPoint,
  level: number,
  radius: number,
  subdivision: number,
  obstacles: readonly DungeonCollisionCircle[],
): ContinuousPoint {
  let point = { ...start };
  for (let pass = 0; pass < 4; pass += 1) {
    let changed = false;
    for (const obstacle of obstacles) {
      const dx = point.x - obstacle.x;
      const dy = point.y - obstacle.y;
      const distance = Math.hypot(dx, dy);
      const required = radius + obstacle.radius;
      if (distance >= required - 1e-9) continue;
      const unit =
        distance > 1e-9
          ? { x: dx / distance, y: dy / distance }
          : { x: -1, y: 0 };
      const correction = required - distance + 1e-7;
      const directions = [
        unit,
        { x: -unit.y, y: unit.x },
        { x: unit.y, y: -unit.x },
      ];
      let best = point;
      let bestDistance = distance;
      for (const direction of directions) {
        const candidate = sweepDungeonDisplacement(
          world,
          point,
          {
            x: direction.x * correction,
            y: direction.y * correction,
          },
          { level, radius, subdivision },
        );
        const candidateDistance = Math.hypot(
          candidate.x - obstacle.x,
          candidate.y - obstacle.y,
        );
        if (candidateDistance > bestDistance) {
          best = candidate;
          bestDistance = candidateDistance;
        }
      }
      if (bestDistance > distance + 1e-9) {
        point = best;
        changed = true;
      }
    }
    if (!changed) break;
  }
  return point;
}

/**
 * Consume grid-planned waypoints through continuous dungeon coordinates.
 *
 * Route cells remain integer terrain and encounter inputs. This integration
 * never quantizes the starting coordinate, so replacing a route while the
 * enemy is between cells changes direction without snapping it to a center.
 */
export function advanceDungeonEnemyRoute(
  world: World,
  start: ContinuousPoint,
  route: readonly TilePoint[],
  elapsedMs: number,
  options: {
    level?: number;
    speed?: number;
    radius?: number;
    subdivision?: number;
    target?: ContinuousPoint;
    stoppingRange?: number;
    obstacles?: readonly DungeonCollisionCircle[];
  } = {},
): DungeonEnemyRouteResult {
  const remainingRoute = route.map((point) => ({ ...point }));
  const level = options.level ?? 0;
  const speed = options.speed ?? DUNGEON_ENEMY_SPEED_TILES_PER_SECOND;
  const radius = options.radius ?? DUNGEON_ENEMY_COLLISION_RADIUS_TILES;
  const subdivision =
    options.subdivision ?? DUNGEON_ENEMY_COLLISION_SUBDIVISION_TILES;
  const stoppingRange =
    options.stoppingRange ?? DUNGEON_ENEMY_MELEE_STOPPING_RANGE_TILES;
  let distanceBudget = Math.max(0, speed * elapsedMs / 1000);
  let point = separateDungeonCircles(
    world,
    start,
    level,
    radius,
    subdivision,
    options.obstacles ?? [],
  );
  let facing: Direction | undefined;
  let moving =
    Math.hypot(point.x - start.x, point.y - start.y) > 1e-9;
  if (
    remainingRoute.length === 0 ||
    elapsedMs <= 0 ||
    !Number.isFinite(elapsedMs)
  ) {
    return { ...point, route: remainingRoute, moving };
  }

  while (distanceBudget > 1e-9 && remainingRoute.length > 0) {
    const finalWaypoint = remainingRoute.length === 1;
    const destination =
      finalWaypoint && options.target
        ? options.target
        : remainingRoute[0]!;
    const dx = destination.x - point.x;
    const dy = destination.y - point.y;
    const distance = Math.hypot(dx, dy);
    const distanceToTravel = finalWaypoint && options.target
      ? Math.max(0, distance - stoppingRange)
      : distance;
    if (distanceToTravel <= 1e-9) {
      if (!finalWaypoint) remainingRoute.shift();
      break;
    }
    const stepDistance = Math.min(distanceBudget, distanceToTravel);
    const nextFacing = directionForDelta(dx, dy);
    if (nextFacing !== null) facing = nextFacing;
    const next = sweepDungeonDisplacement(
      world,
      point,
      {
        x: dx / distance * stepDistance,
        y: dy / distance * stepDistance,
      },
      { level, radius, subdivision, obstacles: options.obstacles },
    );
    const travelled = Math.hypot(next.x - point.x, next.y - point.y);
    point = next;
    if (travelled <= 1e-9) break;
    moving = true;
    distanceBudget -= travelled;
    if (travelled + 1e-7 < stepDistance) break;
    if (stepDistance + 1e-7 >= distanceToTravel && !finalWaypoint) {
      remainingRoute.shift();
    } else {
      break;
    }
  }

  return {
    ...point,
    ...(facing !== undefined ? { facing } : {}),
    route: remainingRoute,
    moving,
  };
}
